import { memo, useCallback, useEffect, useRef, useState } from 'react'
import {
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Maximize, Settings2, Film, Music,
} from 'lucide-react'
import { formatTimecode } from './mediaLibraryFormat.js'

const SPEEDS = [1, 2, 0.5] as const

interface MediaPlayerProps {
  src:         string
  kind:        'video' | 'image' | 'audio' | 'other'
  alt:         string
  /** Cadence pour le timecode et le pas image par image. */
  fps?:        number | null
  /** Durée connue par l'analyse, affichée avant le chargement du flux. */
  durationSec?: number | null
}

/**
 * Lecteur de l'aperçu : transport compact au timecode image par image. Le
 * timecode et la tête de lecture sont réécrits directement dans le DOM à chaque
 * frame pour ne pas rendre tout le composant 25 fois par seconde.
 */
export const MediaPlayer = memo(function MediaPlayer({ src, kind, alt, fps, durationSec }: MediaPlayerProps) {
  const rate = fps && fps > 0 ? fps : 25
  const shellRef  = useRef<HTMLDivElement>(null)
  const videoRef  = useRef<HTMLVideoElement>(null)
  const tcRef     = useRef<HTMLSpanElement>(null)
  const playedRef = useRef<HTMLSpanElement>(null)
  const seekRef   = useRef<HTMLInputElement>(null)
  const [playing,  setPlaying]  = useState(false)
  const [muted,    setMuted]    = useState(false)
  const [speed,    setSpeed]    = useState<(typeof SPEEDS)[number]>(1)
  const [measured, setDuration] = useState<number>(0)
  // Avant le chargement du flux, la durée de l'analyse fait foi.
  const duration = measured || durationSec || 0
  const [buffered, setBuffered] = useState(0)
  const [failed,   setFailed]   = useState(false)

  // Le parent remonte le lecteur à chaque fichier (`key`) : aucun état à remettre à zéro.

  const paint = useCallback(() => {
    const v = videoRef.current
    if (!v) return
    const total = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : duration
    if (tcRef.current) tcRef.current.textContent = formatTimecode(v.currentTime, rate)
    const ratio = total > 0 ? v.currentTime / total : 0
    if (playedRef.current) playedRef.current.style.width = `${ratio * 100}%`
    if (seekRef.current && document.activeElement !== seekRef.current) seekRef.current.value = String(Math.round(ratio * 1000))
  }, [duration, rate])

  useEffect(() => {
    if (!playing) return
    let frame = 0
    const loop = () => { paint(); frame = requestAnimationFrame(loop) }
    frame = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(frame)
  }, [playing, paint])

  const toggle = useCallback(() => {
    const v = videoRef.current
    if (!v) return
    if (v.paused) void v.play().catch(() => setFailed(true))
    else v.pause()
  }, [])

  const step = useCallback((frames: number) => {
    const v = videoRef.current
    if (!v) return
    v.pause()
    v.currentTime = Math.max(0, Math.min((v.duration || duration), v.currentTime + frames / rate))
    paint()
  }, [duration, paint, rate])

  const onSeek = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const v = videoRef.current
    const value = Number(event.target.value)
    if (v && Number.isFinite(v.duration)) { v.currentTime = (value / 1000) * v.duration; paint() }
  }, [paint])

  const onProgress = useCallback(() => {
    const v = videoRef.current
    if (!v || !v.buffered.length || !Number.isFinite(v.duration) || v.duration <= 0) return
    setBuffered((v.buffered.end(v.buffered.length - 1) / v.duration) * 100)
  }, [])

  const cycleSpeed = useCallback(() => {
    setSpeed(prev => {
      const next = SPEEDS[(SPEEDS.indexOf(prev) + 1) % SPEEDS.length]
      if (videoRef.current) videoRef.current.playbackRate = next
      return next
    })
  }, [])

  const fullscreen = useCallback(() => {
    const el = shellRef.current
    if (!el) return
    if (document.fullscreenElement) void document.exitFullscreen()
    else void el.requestFullscreen?.().catch(() => {})
  }, [])

  const onKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'k') { event.preventDefault(); toggle() }
    else if (event.key === 'ArrowLeft') { event.preventDefault(); step(-1) }
    else if (event.key === 'ArrowRight') { event.preventDefault(); step(1) }
  }, [step, toggle])

  if (kind === 'other' || failed) {
    return (
      <div className="media-player media-player--none">
        <span className="media-player__none-icon" aria-hidden><Film size={20} /></span>
        <p className="media-player__none-title">Aperçu indisponible pour ce format dans le navigateur</p>
        <p className="media-player__none-sub">Le fichier reste diffusable par CasparCG.</p>
      </div>
    )
  }

  // Musique : le lecteur du navigateur suffit (pas d'image, pas de timecode).
  if (kind === 'audio') {
    return (
      <div className="media-player media-player--audio">
        <span className="media-player__none-icon" aria-hidden><Music size={20} /></span>
        <audio className="media-player__audio" src={src} controls preload="metadata" aria-label={alt} onError={() => setFailed(true)} />
      </div>
    )
  }

  if (kind === 'image') {
    return (
      <div className="media-player media-player--image">
        <img className="media-player__image" src={src} alt={alt} onError={() => setFailed(true)} />
      </div>
    )
  }

  return (
    <div className="media-player" ref={shellRef} onKeyDown={onKeyDown}>
      <div className="media-player__stage" onClick={toggle}>
        <video
          ref={videoRef}
          className="media-player__video"
          src={src}
          preload="metadata"
          muted={muted}
          playsInline
          onPlay={() => setPlaying(true)}
          onPause={() => { setPlaying(false); paint() }}
          onEnded={() => { setPlaying(false); paint() }}
          onLoadedMetadata={e => { if (Number.isFinite(e.currentTarget.duration)) setDuration(e.currentTarget.duration); paint() }}
          onProgress={onProgress}
          onTimeUpdate={() => { if (!playing) paint() }}
          onError={() => setFailed(true)}
        />
        {!playing && (
          <span className="media-player__big-play" aria-hidden>
            <Play size={22} />
          </span>
        )}
      </div>

      <div className="media-player__transport">
        <div className="media-player__scrubber">
          <span className="media-player__buffered" style={{ width: `${buffered}%` }} aria-hidden />
          <span className="media-player__played" ref={playedRef} aria-hidden />
          <input
            ref={seekRef}
            type="range"
            className="media-player__seek"
            min={0}
            max={1000}
            defaultValue={0}
            onChange={onSeek}
            aria-label="Position de lecture"
          />
        </div>
        <div className="media-player__controls">
          <button type="button" className="media-player__btn" onClick={toggle} aria-label={playing ? 'Pause' : 'Lecture'}>
            {playing ? <Pause size={15} aria-hidden /> : <Play size={15} aria-hidden />}
          </button>
          <button type="button" className="media-player__btn" onClick={() => step(-1)} aria-label="Image précédente">
            <SkipBack size={14} aria-hidden />
          </button>
          <button type="button" className="media-player__btn" onClick={() => step(1)} aria-label="Image suivante">
            <SkipForward size={14} aria-hidden />
          </button>
          <span className="media-player__tc">
            <span ref={tcRef}>{formatTimecode(0, rate)}</span>
            <span aria-hidden> / </span>
            <span>{formatTimecode(duration, rate)}</span>
          </span>
          <span className="media-player__spacer" />
          <button type="button" className="media-player__btn" onClick={() => setMuted(m => !m)} aria-label={muted ? 'Rétablir le son' : 'Couper le son'} aria-pressed={muted}>
            {muted ? <VolumeX size={15} aria-hidden /> : <Volume2 size={15} aria-hidden />}
          </button>
          <button type="button" className="media-player__btn media-player__btn--speed" onClick={cycleSpeed} aria-label={`Vitesse de lecture ${speed}×`} title={`Vitesse ${speed}×`}>
            <Settings2 size={14} aria-hidden />
            {speed !== 1 && <span className="media-player__speed">{String(speed).replace('.', ',')}×</span>}
          </button>
          <button type="button" className="media-player__btn" onClick={fullscreen} aria-label="Plein écran">
            <Maximize size={14} aria-hidden />
          </button>
        </div>
      </div>
    </div>
  )
})
